/**
 * Gestor de sincronización offline-to-online
 * 
 * Responsabilidades:
 * - Detectar cambios de conexión
 * - Mantener cola de operaciones pendientes
 * - Retryar operaciones con backoff exponencial
 * - Resolver conflictos (Last-Write-Wins)
 * - Emitir eventos de sincronización
 */

import eventBus, { EVENTS } from './eventBus.js';
import stateManager from './stateManager.js';
import { isOnline, onConnectionChange } from '../utils/network.js';
import FirestoreService from '../services/firestore.service.js';

class SyncManager {
  constructor() {
    this.isOnline = isOnline();
    this.isSyncing = false;
    this.retryQueue = []; // Cola de reintentos
    this.retryAttempts = new Map(); // ID -> número de intentos
    this.maxRetries = 3;
    this.baseRetryDelay = 1000; // 1 segundo

    // Escuchar cambios de conexión
    this.#setupConnectionListener();

    // Escuchar cambios de estado para encolar operaciones
    this.#setupStateListener();

    // Registrar Background Sync si está disponible
    this.#setupBackgroundSync();
  }

  #setupConnectionListener() {
    onConnectionChange((online) => {
      const wasOnline = this.isOnline;
      this.isOnline = online;

      if (online && !wasOnline) {
        console.log('✓ Conexión restaurada');
        eventBus.emit(EVENTS['online:connected']);
        this.syncPendingOps();
      } else if (!online && wasOnline) {
        console.log('⊘ Se perdió la conexión');
        eventBus.emit(EVENTS['offline:disconnected']);
      }
    });
  }

  #setupStateListener() {
    // Escuchar cambios de estado para detectar nuevas operaciones
    stateManager.subscribe('sync.pendingOps', (ops) => {
      // Si hay ops pendientes y estamos online, intentar sincronizar
      if (ops.length > 0 && this.isOnline && !this.isSyncing) {
        // Esperar un poco antes de sincronizar (para batching)
        setTimeout(() => this.syncPendingOps(), 500);
      }
    });
  }

  #setupBackgroundSync() {
    // Registrar service worker background sync si está disponible
    if ('serviceWorker' in navigator && 'SyncManager' in window) {
      navigator.serviceWorker.ready.then(registration => {
        // Intentar registrar sync cuando hay ops pendientes
        const checkAndSync = () => {
          const ops = stateManager.getPendingOps();
          if (ops.length > 0 && this.isOnline) {
            registration.sync.register('sync-firebase').catch(err => {
              console.log('No se pudo registrar background sync:', err);
            });
          }
        };

        // Chequear periodicamente (cada 30 segundos)
        setInterval(checkAndSync, 30000);
      });
    }
  }

  /**
   * Sincronizar operaciones pendientes
   * @param {Object} options - Opciones de sincronización
   */
  async syncPendingOps(options = {}) {
    if (this.isSyncing || !this.isOnline) {
      return;
    }

    const pendingOps = stateManager.getPendingOps();
    if (pendingOps.length === 0) {
      return;
    }

    this.isSyncing = true;
    stateManager.setState('sync.isSyncing', true);
    eventBus.emit(EVENTS['sync:started'], { operationCount: pendingOps.length });

    let syncedCount = 0;
    const errors = [];

    for (const operation of pendingOps) {
      try {
        // Aquí se llamaría al servicio de Firestore para ejecutar la operación
        // Por ahora, asumimos éxito
        await this.#executeOperation(operation);
        syncedCount++;
        stateManager.removePendingOp(operation.id);
      } catch (error) {
        console.error(`Error sincronizando operación ${operation.id}:`, error);
        errors.push({ operationId: operation.id, error: error.message });

        // Reintentar con backoff
        this.#scheduleRetry(operation);
      }
    }

    this.isSyncing = false;
    stateManager.setState('sync.isSyncing', false);
    stateManager.setState('sync.lastSyncTime', new Date().toISOString());

    eventBus.emit(EVENTS['sync:completed'], {
      syncedCount,
      totalCount: pendingOps.length,
      errors
    });

    if (errors.length > 0) {
      eventBus.emit(EVENTS['sync:error'], {
        message: `${errors.length} operaciones no pudieron sincronizarse`,
        errors
      });
    }
  }

  /**
   * Ejecutar una operación contra Firestore
   */
  async #executeOperation(operation) {
    const { type, collection, documentId, data, offlineId, entityName } =
      operation;
    const opts = entityName ? { entityName } : {};

    switch (type) {
      case 'create':
        if (offlineId) {
          stateManager.setEntity(entityName || collection, offlineId, null, 'delete');
        }
        await FirestoreService.create(collection, data, opts);
        break;
      case 'update':
        await FirestoreService.update(collection, documentId, data, opts);
        break;
      case 'delete':
        await FirestoreService.delete(collection, documentId, opts);
        break;
      default:
        throw new Error(`Operación desconocida: ${type}`);
    }
  }

  /**
   * Programar reintento de operación fallida
   */
  #scheduleRetry(operation) {
    const attemptCount = (this.retryAttempts.get(operation.id) || 0) + 1;

    if (attemptCount > this.maxRetries) {
      console.error(`Máximo número de reintentos alcanzado para ${operation.id}`);
      eventBus.emit(EVENTS['sync:error'], {
        message: `No se pudo sincronizar operación ${operation.id} después de ${this.maxRetries} intentos`,
        operationId: operation.id
      });
      return;
    }

    this.retryAttempts.set(operation.id, attemptCount);

    // Backoff exponencial: 1s, 2s, 4s
    const delay = this.baseRetryDelay * Math.pow(2, attemptCount - 1);

    setTimeout(() => {
      if (this.isOnline) {
        this.#executeOperation(operation)
          .then(() => {
            stateManager.removePendingOp(operation.id);
            this.retryAttempts.delete(operation.id);
            console.log(`✓ Operación reintentada exitosamente: ${operation.id}`);
          })
          .catch(error => {
            console.error(`Reintento fallido para ${operation.id}:`, error);
            this.#scheduleRetry(operation);
          });
      }
    }, delay);

    console.log(`⟲ Reintentando ${operation.id} en ${delay}ms (intento ${attemptCount}/${this.maxRetries})`);
  }

  /**
   * Agregar operación a la cola
   * @param {Object} operation - Operación a encolar
   */
  addOperation(operation) {
    const opWithId = {
      ...operation,
      id: operation.id || `op_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
    };

    stateManager.addPendingOp(opWithId);

    // Si estamos online, intentar sincronizar inmediatamente
    if (this.isOnline && !this.isSyncing) {
      setTimeout(() => this.syncPendingOps(), 100);
    }
  }

  /**
   * Obtener estado de sincronización
   */
  getStatus() {
    return {
      isOnline: this.isOnline,
      isSyncing: this.isSyncing,
      pendingOpsCount: stateManager.getPendingOps().length,
      lastSyncTime: stateManager.getStateAt('sync.lastSyncTime')
    };
  }
}

// Singleton global
const syncManager = new SyncManager();

export default syncManager;
